/**
 * IdpDetail — edit competencies, goals, assign courses.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Target, Plus, Trash2, CheckCircle2, BookOpen, Sparkles, Zap } from "lucide-react";

function newId() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

export default function IdpDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [plan, setPlan] = useState(null);
  const [courses, setCourses] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCourseId, setAssignCourseId] = useState("");
  const [assignMandatory, setAssignMandatory] = useState(false);
  const [assignDue, setAssignDue] = useState("");
  // AI path recommender
  const [pathOpen, setPathOpen] = useState(false);
  const [pathBusy, setPathBusy] = useState(false);
  const [pathResult, setPathResult] = useState(null);
  const [pathSelected, setPathSelected] = useState({});

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/idp/plans/${id}`);
      setPlan(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId, id]);
  useEffect(() => { load(); }, [load]);

  const loadCourses = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/courses`);
      setCourses(Array.isArray(data) ? data : []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { if (assignOpen) loadCourses(); }, [assignOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/idp/plans/${id}`, body);
      setPlan(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const addCompetency = () => {
    const list = [...(plan.competencies || []), { id: newId(), name: "New competency", current_level: 1, target_level: 3, notes: "" }];
    patch({ competencies: list });
  };
  const updateCompetency = (i, patchObj) => {
    const list = [...(plan.competencies || [])];
    list[i] = { ...list[i], ...patchObj };
    patch({ competencies: list });
  };
  const deleteCompetency = (i) => {
    const list = [...(plan.competencies || [])];
    list.splice(i, 1);
    patch({ competencies: list });
  };
  const addGoal = () => {
    const list = [...(plan.goals || []), { id: newId(), title: "New goal", description: "", due_date: "", done: false }];
    patch({ goals: list });
  };
  const updateGoal = (i, patchObj) => {
    const list = [...(plan.goals || [])];
    list[i] = { ...list[i], ...patchObj };
    patch({ goals: list });
  };
  const deleteGoal = (i) => {
    const list = [...(plan.goals || [])];
    list.splice(i, 1);
    patch({ goals: list });
  };

  const assignCourse = async () => {
    if (!assignCourseId) return;
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/idp/plans/${id}/courses`,
        { course_id: assignCourseId, mandatory: assignMandatory, due_date: assignDue || null });
      setPlan(data);
      setAssignOpen(false);
      setAssignCourseId("");
      toast.success("Course assigned");
    } catch (e) { toast.error(apiErrorMessage(e, "Assign failed")); }
  };

  const unassignCourse = async (courseId) => {
    if (!confirm("Remove this course from the plan?")) return;
    try {
      const { data } = await api.delete(`/workspaces/${activeWorkspaceId}/idp/plans/${id}/courses/${courseId}`);
      setPlan(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  const suggestPath = async () => {
    setPathOpen(true);
    setPathBusy(true);
    setPathResult(null);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/idp/plans/${id}/suggest-path`, {});
      setPathResult(data);
      // Preselect all library-matched items by default
      const sel = {};
      (data.path || []).forEach((p, i) => {
        if (!p.external && p.course_id) sel[i] = true;
      });
      setPathSelected(sel);
    } catch (e) { toast.error(apiErrorMessage(e, "AI suggestion failed")); }
    finally { setPathBusy(false); }
  };

  const applyPath = async () => {
    const ids = (pathResult?.path || [])
      .filter((p, i) => pathSelected[i] && !p.external && p.course_id)
      .map((p) => p.course_id);
    if (!ids.length) return toast.error("Select at least one library course to apply");
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/idp/plans/${id}/apply-path`,
        { course_ids: ids });
      toast.success(`${data.added.length} course(s) added (${data.skipped.length} skipped)`);
      // reload plan
      const { data: fresh } = await api.get(`/workspaces/${activeWorkspaceId}/idp/plans/${id}`);
      setPlan(fresh);
      setPathOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
  };

  if (!plan) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title={plan.title}
        description={`${plan.user_name} · Target: ${plan.target_role || "—"} · ${plan.status}`}
        actions={
          <Select value={plan.status || "draft"} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger className="h-9 rounded-none w-32" data-testid="idp-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <div className="border border-border p-3">
        <div className="w-full h-2 bg-muted"><div className="h-2 bg-emerald-500" style={{ width: `${plan.progress_percent || 0}%` }} /></div>
        <div className="text-[10px] text-muted-foreground mt-1">Overall progress: {plan.progress_percent || 0}%</div>
      </div>

      {/* Header inputs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] label-eyebrow">Title</Label>
          <Input defaultValue={plan.title} onBlur={(e) => e.target.value !== plan.title && patch({ title: e.target.value })} className="h-8 rounded-none" data-testid="idp-title" />
        </div>
        <div>
          <Label className="text-[10px] label-eyebrow">Target role</Label>
          <Input defaultValue={plan.target_role || ""} onBlur={(e) => e.target.value !== (plan.target_role || "") && patch({ target_role: e.target.value })} className="h-8 rounded-none" />
        </div>
        <div>
          <Label className="text-[10px] label-eyebrow">Target date</Label>
          <Input type="date" defaultValue={(plan.timeline_end || "").split("T")[0] || ""} onBlur={(e) => e.target.value !== (plan.timeline_end || "").split("T")[0] && patch({ timeline_end: e.target.value })} className="h-8 rounded-none" />
        </div>
      </div>

      {/* Competencies */}
      <section className="border border-border p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Competencies</h3>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={addCompetency} data-testid="idp-add-competency"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        {(plan.competencies || []).length === 0 && <div className="text-xs text-muted-foreground italic">None yet.</div>}
        {(plan.competencies || []).map((c, i) => (
          <div key={c.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`idp-comp-${i}`}>
            <Input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && updateCompetency(i, { name: e.target.value })} className="h-8 rounded-none col-span-5" />
            <div className="col-span-2">
              <Label className="text-[9px] label-eyebrow">Current</Label>
              <Input type="number" min={1} max={5} defaultValue={c.current_level} onBlur={(e) => updateCompetency(i, { current_level: Number(e.target.value) })} className="h-8 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label className="text-[9px] label-eyebrow">Target</Label>
              <Input type="number" min={1} max={5} defaultValue={c.target_level} onBlur={(e) => updateCompetency(i, { target_level: Number(e.target.value) })} className="h-8 rounded-none" />
            </div>
            <Input defaultValue={c.notes || ""} onBlur={(e) => updateCompetency(i, { notes: e.target.value })} placeholder="notes" className="h-8 rounded-none col-span-2 text-xs" />
            <button onClick={() => deleteCompetency(i)} className="text-rose-600 col-span-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </section>

      {/* Goals */}
      <section className="border border-border p-3 space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Goals</h3>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={addGoal} data-testid="idp-add-goal"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        {(plan.goals || []).length === 0 && <div className="text-xs text-muted-foreground italic">None yet.</div>}
        {(plan.goals || []).map((g, i) => (
          <div key={g.id || i} className="border border-border p-2 space-y-1" data-testid={`idp-goal-${i}`}>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={g.done || false} onChange={(e) => updateGoal(i, { done: e.target.checked })} />
              <Input defaultValue={g.title} onBlur={(e) => e.target.value !== g.title && updateGoal(i, { title: e.target.value })} className="h-8 rounded-none text-sm font-semibold flex-1" />
              <Input type="date" defaultValue={(g.due_date || "").split("T")[0] || ""} onBlur={(e) => updateGoal(i, { due_date: e.target.value })} className="h-8 rounded-none w-40" />
              <button onClick={() => deleteGoal(i)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <Textarea defaultValue={g.description || ""} onBlur={(e) => (e.target.value || "") !== (g.description || "") && updateGoal(i, { description: e.target.value })} rows={2} className="rounded-none text-xs" placeholder="Description / success criteria" />
          </div>
        ))}
      </section>

      {/* Courses */}
      <section className="border border-border p-3 space-y-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Assigned courses</h3>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={suggestPath} data-testid="idp-suggest-path"><Sparkles className="w-3 h-3 mr-1" /> Suggest AI path</Button>
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setAssignOpen(true)} data-testid="idp-add-course"><Plus className="w-3 h-3 mr-1" /> Assign course</Button>
        </div>
        {(plan.courses || []).length === 0 && <div className="text-xs text-muted-foreground italic">No courses assigned.</div>}
        {(plan.courses || []).map((c) => (
          <div key={c.course_id} className="border border-border p-2 flex items-center gap-2 text-sm" data-testid={`idp-course-${c.course_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.course_title}</div>
              <div className="text-[10px] text-muted-foreground">{c.course_duration_min || 0}m · due {c.due_date || "—"}</div>
            </div>
            <span className={`chip text-[9px] ${c.mandatory ? "chip-warning" : "chip-info"}`}>{c.mandatory ? "MANDATORY" : "OPTIONAL"}</span>
            <span className="chip text-[9px]">{c.status}</span>
            <div className="w-20 h-2 bg-muted"><div className="h-2 bg-emerald-500" style={{ width: `${c.progress_percent || 0}%` }} /></div>
            <button onClick={() => unassignCourse(c.course_id)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </section>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="idp-assign-dialog">
          <DialogHeader><DialogTitle>Assign a course</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Course *</Label>
              <Select value={assignCourseId} onValueChange={setAssignCourseId}>
                <SelectTrigger className="h-9 rounded-none" data-testid="idp-assign-course"><SelectValue placeholder="Pick a course" /></SelectTrigger>
                <SelectContent>
                  {courses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <Switch checked={assignMandatory} onCheckedChange={setAssignMandatory} data-testid="idp-assign-mandatory" /> Mandatory
            </label>
            <div>
              <Label className="text-xs mb-1 block">Due date</Label>
              <Input type="date" value={assignDue} onChange={(e) => setAssignDue(e.target.value)} className="h-9 rounded-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={assignCourse} data-testid="idp-assign-run">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Learning Path dialog */}
      <Dialog open={pathOpen} onOpenChange={setPathOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="idp-path-dialog">
          <DialogHeader>
            <DialogTitle>AI-suggested learning path</DialogTitle>
            <DialogDescription>
              Claude Sonnet 4.5 analyses the plan&apos;s competency gaps and ranks the best courses from your library to bridge them.
            </DialogDescription>
          </DialogHeader>
          {pathBusy && <div className="text-sm text-muted-foreground">Analysing gaps + ranking library courses (~20-40s)…</div>}
          {!pathBusy && pathResult && (
            <div className="space-y-3">
              {pathResult.overall_rationale && (
                <div className="border-l-2 border-primary/40 bg-primary/5 p-2 text-xs">
                  <div className="label-eyebrow text-primary mb-1">Overall rationale</div>
                  {pathResult.overall_rationale}
                </div>
              )}
              {pathResult.gaps?.length > 0 && (
                <div className="text-xs">
                  <div className="label-eyebrow text-muted-foreground mb-1">Competency gaps ({pathResult.gaps.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {pathResult.gaps.map((g, i) => (
                      <span key={i} className="chip chip-warning text-[10px]">{g.competency}: {g.current} → {g.target}</span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="label-eyebrow text-muted-foreground mb-1">Suggested path ({pathResult.path?.length || 0} items)</div>
                <div className="space-y-2">
                  {(pathResult.path || []).map((p, i) => (
                    <label key={i} className={`block border ${pathSelected[i] ? "border-primary" : "border-border"} p-3 cursor-pointer`} data-testid={`idp-path-item-${i}`}>
                      <div className="flex items-start gap-2">
                        <input type="checkbox" checked={pathSelected[i] || false} disabled={p.external} onChange={(e) => setPathSelected({ ...pathSelected, [i]: e.target.checked })} className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-muted-foreground">#{i + 1}</span>
                            <span className="font-semibold text-sm">{p.title}</span>
                            {p.external ? (
                              <span className="chip chip-danger text-[9px]">EXTERNAL · NOT IN LIBRARY</span>
                            ) : (
                              <span className="chip chip-success text-[9px]">IN LIBRARY</span>
                            )}
                          </div>
                          {p.matches_competencies?.length > 0 && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              Bridges: {p.matches_competencies.join(" · ")}
                            </div>
                          )}
                          {p.rationale && <div className="text-xs mt-1">{p.rationale}</div>}
                          {p.external && p.topic_prompt && (
                            <div className="mt-2 text-[11px] text-muted-foreground bg-muted/40 p-2 border border-dashed border-border">
                              <b>To create:</b> paste this into AI course generator — <em>&ldquo;{p.topic_prompt}&rdquo;</em>
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setPathOpen(false)}>Close</Button>
            {pathResult && (
              <Button className="rounded-none" onClick={applyPath} data-testid="idp-path-apply">
                <Zap className="w-4 h-4 mr-1" /> Apply selected as optional
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
